// Apply colour + typography tokens by injecting a <style> tag at :root.
// Also maps our white-label tokens onto the variables Frappe and its SPAs
// already use, so we don't have to know every individual component class.

const FRAPPE_VAR_ALIASES = {
	"--wl-primary": ["--primary", "--primary-color", "--text-on-blue", "--btn-primary-bg"],
	"--wl-text": ["--text-color"],
	"--wl-link": ["--link-color"],
	"--wl-bg": ["--bg-color", "--gray-50"],
	"--wl-surface": ["--card-bg", "--fg-color"],
	"--wl-border": ["--border-color", "--gray-200"],
	"--wl-muted": ["--text-muted", "--text-light", "--gray-500"],
	"--wl-navbar-bg": ["--navbar-bg"],
	"--wl-sidebar-bg": ["--sidebar-bg", "--sidebar-select-color"],
	"--wl-success": ["--green-500", "--success"],
	"--wl-warning": ["--yellow-500", "--warning"],
	"--wl-danger": ["--red-500", "--danger"],
	"--wl-info": ["--blue-500", "--info"],
	"--wl-radius": ["--border-radius"],
	"--wl-font-body": ["--font-family", "--font-stack"],
	"--wl-font-mono": ["--font-stack-monospace"],
};

function buildRootBlock(vars) {
	const lines = [];
	for (const [token, value] of Object.entries(vars)) {
		lines.push(`${token}: ${value};`);
		for (const alias of FRAPPE_VAR_ALIASES[token] || []) {
			lines.push(`${alias}: ${value};`);
		}
	}
	return `:root { ${lines.join(" ")} }`;
}

function buildComponentRules(vars) {
	const rules = [];
	if (vars["--wl-font-body"]) {
		rules.push(`body, .frappe-app, .desk-page { font-family: var(--wl-font-body) !important; }`);
	}
	if (vars["--wl-font-size"]) {
		rules.push(`body { font-size: var(--wl-font-size) !important; }`);
	}
	if (vars["--wl-font-heading"]) {
		rules.push(`h1, h2, h3, h4, h5, h6, .heading { font-family: var(--wl-font-heading) !important; }`);
	}
	if (vars["--wl-heading-weight"]) {
		rules.push(`h1, h2, h3, h4, h5, h6 { font-weight: var(--wl-heading-weight) !important; }`);
	}
	if (vars["--wl-font-mono"]) {
		rules.push(`code, pre, kbd, samp { font-family: var(--wl-font-mono) !important; }`);
	}
	if (vars["--wl-radius"]) {
		rules.push(`.btn, .form-control, .card, .modal-content, input, select, textarea { border-radius: var(--wl-radius) !important; }`);
	}
	if (vars["--wl-shadow"]) {
		rules.push(`.card, .widget, .modal-content, .dropdown-menu { box-shadow: var(--wl-shadow) !important; }`);
	}
	if (vars["--wl-navbar-bg"]) {
		rules.push(`.navbar, header.navbar, .navbar-default { background-color: var(--wl-navbar-bg) !important; }`);
	}
	if (vars["--wl-navbar-text"]) {
		rules.push(`.navbar, .navbar a, .navbar-nav > li > a { color: var(--wl-navbar-text) !important; }`);
	}
	if (vars["--wl-sidebar-bg"]) {
		rules.push(`.sidebar, .layout-side-section, .standard-sidebar { background-color: var(--wl-sidebar-bg) !important; }`);
	}
	if (vars["--wl-sidebar-text"]) {
		rules.push(`.sidebar a, .layout-side-section a, .standard-sidebar a { color: var(--wl-sidebar-text) !important; }`);
	}
	if (vars["--wl-navbar-height"]) {
		rules.push(`.navbar, header.navbar { height: var(--wl-navbar-height) !important; min-height: var(--wl-navbar-height) !important; }`);
	}
	if (vars["--wl-sidebar-width"]) {
		rules.push(`.layout-side-section, .standard-sidebar { width: var(--wl-sidebar-width) !important; }`);
	}
	if (vars["--wl-primary"]) {
		rules.push(`.btn-primary, .btn-primary:focus { background-color: var(--wl-primary) !important; border-color: var(--wl-primary) !important; }`);
		rules.push(`a, .text-primary { color: var(--wl-link, var(--wl-primary)); }`);
	}
	return rules.join("\n");
}

function ensureFontLink(url) {
	if (!url) return;
	if (document.querySelector(`link[data-wl="font"][href="${url}"]`)) return;
	const link = document.createElement("link");
	link.rel = "stylesheet";
	link.href = url;
	link.dataset.wl = "font";
	document.head.appendChild(link);
}

export function installTheme(theme) {
	if (!theme || !theme.enabled) return;
	const vars = theme.vars || {};
	if (!Object.keys(vars).length && !theme.extra_css_variables && !theme.font_import_url) return;

	ensureFontLink(theme.font_import_url);

	let css = buildRootBlock(vars);
	if (theme.extra_css_variables) {
		css += `\n:root { ${theme.extra_css_variables} }`;
	}
	css += "\n" + buildComponentRules(vars);

	const id = "wl-theme-style";
	let style = document.getElementById(id);
	if (!style) {
		style = document.createElement("style");
		style.id = id;
		document.head.appendChild(style);
	}
	style.textContent = css;
}
